import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { Button, Calendar, DateField, DatePicker, EmptyState, Label, ListBox, Modal, Select, TimeField } from '@heroui/react'
import { parseDate, parseDateTime, parseTime, type CalendarDate, type CalendarDateTime } from '@internationalized/date'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'

// HeroUI pieces the Signage tabs share: the dialog, the upload button, a select and the date and time fields.

/** A Signage dialog, open while mounted. Escape closes it unless an inner picker is open and takes it. */
export function SignageModal({
  title,
  onClose,
  size = 'md',
  foot,
  footNote,
  children,
}: {
  title: string
  onClose: () => void
  size?: 'md' | 'lg'
  foot?: ReactNode
  footNote?: ReactNode
  children: ReactNode
}) {
  const { say } = useStaffText()
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return
    // The branch picker (ui/MultiSelect) closes itself on Escape; the dialog stays.
    if (e.currentTarget.querySelector('[aria-expanded="true"]')) return
    onClose()
  }
  return (
    <Modal.Backdrop isOpen isKeyboardDismissDisabled onOpenChange={(open) => !open && onClose()}>
      <Modal.Container size={size} scroll="inside">
        <Modal.Dialog className={size === 'lg' ? 'signage-modal signage-modal--lg' : 'signage-modal'}>
          <div className="signage-modal__keys" onKeyDown={onKeyDown}>
            <Modal.CloseTrigger aria-label={say('signage.close')} />
            <Modal.Header>
              <Modal.Heading className="signage-modal__title">{title}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="signage-modal__body">{children}</Modal.Body>
            {(foot || footNote) && (
              <Modal.Footer className="signage-modal__foot">
                {footNote && <span className="signage-modal__note">{footNote}</span>}
                {foot}
              </Modal.Footer>
            )}
          </div>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/** Nothing to list yet: an icon, the words and the one action. */
export function Empty({ icon, title, action }: { icon: IconName; title: string; action?: ReactNode }) {
  return (
    <EmptyState className="signage-empty">
      <span className="signage-empty__icon" aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <p className="signage-empty__title">{title}</p>
      {action}
    </EmptyState>
  )
}

/** Upload as a HeroUI button over a hidden file input; the input stays for its dialog and for e2e. */
export function UploadButton({
  accept,
  label,
  disabled,
  variant = 'secondary',
  onFiles,
}: {
  accept: string
  label: string
  disabled?: boolean
  variant?: 'primary' | 'secondary'
  onFiles: (files: File[]) => void
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <span className="signage-upload-btn">
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        accept={accept}
        multiple
        disabled={disabled}
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length > 0) onFiles(files)
        }}
      />
      <Button variant={variant} size="sm" isDisabled={disabled} onPress={() => input.current?.click()}>
        <Icon name="upload" size={16} /> {label}
      </Button>
    </span>
  )
}

/** One choice from a short list, as a HeroUI Select; a visible label, or only an accessible one. */
export function PickOne({
  value,
  onChange,
  options,
  label,
  hideLabel,
  className,
}: {
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<{ value: string; label: string }>
  label: string
  hideLabel?: boolean
  className?: string
}) {
  const { say } = useStaffText()
  return (
    <Select
      className={className ?? 'signage-select'}
      variant="secondary"
      aria-label={hideLabel ? label : undefined}
      placeholder={say('common.choose')}
      value={value || null}
      onChange={(next) => next != null && onChange(String(next))}
    >
      {!hideLabel && <Label>{label}</Label>}
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={o.value} id={o.value} textValue={o.label}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

const asDay = (s: string): CalendarDate | null => {
  try {
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? parseDate(s) : null
  } catch {
    return null
  }
}
const asMoment = (s: string): CalendarDateTime | null => {
  try {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) ? parseDateTime(s.slice(0, 16)) : null
  } catch {
    return null
  }
}

function PickerCalendar({ label }: { label: string }) {
  return (
    <Calendar aria-label={label}>
      <Calendar.Header>
        <Calendar.YearPickerTrigger>
          <Calendar.YearPickerTriggerHeading />
          <Calendar.YearPickerTriggerIndicator />
        </Calendar.YearPickerTrigger>
        <Calendar.NavButton slot="previous" />
        <Calendar.NavButton slot="next" />
      </Calendar.Header>
      <Calendar.Grid>
        <Calendar.GridHeader>{(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}</Calendar.GridHeader>
        <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
      </Calendar.Grid>
      <Calendar.YearPickerGrid>
        <Calendar.YearPickerGridBody>{({ year }) => <Calendar.YearPickerCell year={year} />}</Calendar.YearPickerGridBody>
      </Calendar.YearPickerGrid>
    </Calendar>
  )
}

/** A calendar day as YYYY-MM-DD, the value a date input held; '' while it is not a whole date. */
export function DayField({ label, value, min, onChange }: { label: string; value: string; min?: string; onChange: (day: string) => void }) {
  return (
    <DatePicker
      className="signage-date"
      value={asDay(value)}
      minValue={min ? asDay(min) ?? undefined : undefined}
      onChange={(d) => onChange(d ? d.toString() : '')}
    >
      <Label>{label}</Label>
      <DateField.Group fullWidth variant="secondary">
        <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
        <DateField.Suffix>
          <DatePicker.Trigger>
            <DatePicker.TriggerIndicator />
          </DatePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      <DatePicker.Popover>
        <PickerCalendar label={label} />
      </DatePicker.Popover>
    </DatePicker>
  )
}

/** A branch wall time as YYYY-MM-DDTHH:MM, the value a datetime-local input held; '' when cleared. */
export function MomentField({ label, value, min, max, onChange }: { label: string; value: string; min: string; max: string; onChange: (at: string) => void }) {
  return (
    <DatePicker
      className="signage-date signage-moment"
      aria-label={label}
      granularity="minute"
      hourCycle={24}
      value={asMoment(value)}
      minValue={asMoment(min) ?? undefined}
      maxValue={asMoment(max) ?? undefined}
      onChange={(d) => onChange(d ? d.toString().slice(0, 16) : '')}
    >
      <DateField.Group fullWidth variant="secondary">
        <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
        <DateField.Suffix>
          <DatePicker.Trigger>
            <DatePicker.TriggerIndicator />
          </DatePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      <DatePicker.Popover>
        <PickerCalendar label={label} />
      </DatePicker.Popover>
    </DatePicker>
  )
}

/** A time of day as HH:MM, the value a time input held; '' while it is not whole. */
export function ClockField({ label, value, onChange }: { label: string; value: string; onChange: (hhmm: string) => void }) {
  let shown = null
  try {
    shown = /^\d{2}:\d{2}$/.test(value) ? parseTime(value) : null
  } catch {
    shown = null
  }
  return (
    <TimeField className="signage-time" hourCycle={24} value={shown} onChange={(t) => onChange(t ? t.toString().slice(0, 5) : '')}>
      <Label>{label}</Label>
      <TimeField.Group variant="secondary">
        <TimeField.Input>{(segment) => <TimeField.Segment segment={segment} />}</TimeField.Input>
      </TimeField.Group>
    </TimeField>
  )
}
